/**
 * useAircraftPhoto Hook
 *
 * Fetches aircraft photos from planespotters.net by ICAO24 hex code.
 * Includes an in-memory LRU cache to avoid repeated lookups.
 *
 * API: https://api.planespotters.net/pub/photos/hex/{icao24}
 * Free, no auth required. Returns community-submitted aircraft photos.
 *
 * Note: Coverage for Australian aircraft is limited as photos are community-submitted.
 */

import { useState, useEffect, useRef } from 'react';

export interface AircraftPhotoResult {
  photoUrl: string | null;
  thumbnailUrl: string | null;
  photographer: string | null;
  link: string | null;
  isLoading: boolean;
  error: string | null;
}

interface CacheEntry {
  photoUrl: string | null;
  thumbnailUrl: string | null;
  photographer: string | null;
  link: string | null;
}

// Module-level LRU cache shared across all hook instances
const MAX_CACHE_SIZE = 100;
const photoCache = new Map<string, CacheEntry>();
const cacheAccessOrder: string[] = [];

// Track in-flight requests to avoid duplicate fetches
const pendingRequests = new Map<string, Promise<CacheEntry>>();

function getCached(icao24: string): CacheEntry | undefined {
  const entry = photoCache.get(icao24);
  if (entry) {
    // Move to end (most recently used)
    const idx = cacheAccessOrder.indexOf(icao24);
    if (idx !== -1) {
      cacheAccessOrder.splice(idx, 1);
    }
    cacheAccessOrder.push(icao24);
  }
  return entry;
}

function setCached(icao24: string, entry: CacheEntry): void {
  if (photoCache.has(icao24)) {
    const idx = cacheAccessOrder.indexOf(icao24);
    if (idx !== -1) {
      cacheAccessOrder.splice(idx, 1);
    }
  } else if (photoCache.size >= MAX_CACHE_SIZE) {
    // Evict least recently used
    const lru = cacheAccessOrder.shift();
    if (lru) {
      photoCache.delete(lru);
    }
  }
  photoCache.set(icao24, entry);
  cacheAccessOrder.push(icao24);
}

const EMPTY_ENTRY: CacheEntry = { photoUrl: null, thumbnailUrl: null, photographer: null, link: null };

async function fetchPhoto(icao24: string): Promise<CacheEntry> {
  // Check cache first
  const cached = getCached(icao24);
  if (cached) return cached;

  // Check if already fetching
  const pending = pendingRequests.get(icao24);
  if (pending) return pending;

  const promise = (async (): Promise<CacheEntry> => {
    try {
      const response = await fetch(
        `https://api.planespotters.net/pub/photos/hex/${icao24}`,
        { signal: AbortSignal.timeout(8000) }
      );

      if (!response.ok) {
        setCached(icao24, EMPTY_ENTRY);
        return EMPTY_ENTRY;
      }

      const data = await response.json();

      if (data.photos && data.photos.length > 0) {
        const photo = data.photos[0];
        const entry: CacheEntry = {
          photoUrl: photo.src || null,
          thumbnailUrl: photo.thumbnail_large?.src || photo.thumbnail?.src || photo.src || null,
          photographer: photo.photographer || null,
          link: photo.link || null,
        };
        setCached(icao24, entry);
        return entry;
      }

      // No photos found
      setCached(icao24, EMPTY_ENTRY);
      return EMPTY_ENTRY;
    } catch {
      // Network error or timeout — cache as empty to avoid hammering
      setCached(icao24, EMPTY_ENTRY);
      return EMPTY_ENTRY;
    } finally {
      pendingRequests.delete(icao24);
    }
  })();

  pendingRequests.set(icao24, promise);
  return promise;
}

export function useAircraftPhoto(icao24: string | null): AircraftPhotoResult {
  const [result, setResult] = useState<AircraftPhotoResult>({
    photoUrl: null,
    thumbnailUrl: null,
    photographer: null,
    link: null,
    isLoading: false,
    error: null,
  });

  const currentIcaoRef = useRef<string | null>(null);

  useEffect(() => {
    if (!icao24) {
      setResult({
        photoUrl: null,
        thumbnailUrl: null,
        photographer: null,
        link: null,
        isLoading: false,
        error: null,
      });
      currentIcaoRef.current = null;
      return;
    }

    // Check cache synchronously first
    const cached = getCached(icao24);
    if (cached) {
      setResult({
        ...cached,
        isLoading: false,
        error: null,
      });
      currentIcaoRef.current = icao24;
      return;
    }

    // Fetch asynchronously
    currentIcaoRef.current = icao24;
    setResult(prev => ({ ...prev, isLoading: true, error: null }));

    fetchPhoto(icao24).then(entry => {
      // Only update if still the same aircraft
      if (currentIcaoRef.current === icao24) {
        setResult({
          ...entry,
          isLoading: false,
          error: null,
        });
      }
    }).catch(err => {
      if (currentIcaoRef.current === icao24) {
        setResult({
          photoUrl: null,
          thumbnailUrl: null,
          photographer: null,
          link: null,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Failed to load photo',
        });
      }
    });
  }, [icao24]);

  return result;
}
